import { ArrowDown, ChevronRight, Leaf } from "lucide-react";
import { Button } from "../components/ui/button";
import { useParallax } from "../hooks/useAnimations";
import { useState, useEffect } from "react";

import { assets } from "../lib/assets";

function TypeWriter({ texts, speed = 60, pause = 2200 }) {
  const [displayed, setDisplayed] = useState("");
  const [idx, setIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = texts[idx];
    let timeout;
    if (!deleting && charIdx < current.length) {
      timeout = setTimeout(() => {
        setDisplayed(current.slice(0, charIdx + 1));
        setCharIdx(c => c + 1);
      }, speed);
    } else if (!deleting && charIdx === current.length) {
      timeout = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIdx > 0) {
      timeout = setTimeout(() => {
        setDisplayed(current.slice(0, charIdx - 1));
        setCharIdx(c => c - 1);
      }, speed / 2);
    } else if (deleting && charIdx === 0) {
      setDeleting(false);
      setIdx(i => (i + 1) % texts.length);
    }
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, idx, texts, speed, pause]);

  return (
    <span>
      {displayed}
      <span className="inline-block w-[3px] h-[1em] bg-accent-green ml-0.5 align-middle animate-pulse" />
    </span>
  );
}

export default function Hero({ t, lang }) {
  const offset = useParallax(0.2);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const rotatingWords = lang === "en"
    ? ["Healthier Livestock", "Better Feed Efficiency", "Sustainable Growth"]
    : ["更健康的畜禽", "更高的饲料效率", "可持续增长"];

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <div className="absolute inset-0" style={{ transform: `translateY(${offset}px)` }}>
        <img
          src={assets.heroBg}
          alt="Shandong Prit Biotechnology - science-backed animal nutrition and veterinary feed additives"
          fetchPriority="high"
          className="w-full h-[120%] object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-emerald-950/75" />
      <div className="absolute inset-0 noise-overlay" />

      <div className="absolute bottom-0 left-0 w-full h-[40%] bg-gradient-to-t from-emerald-900/40 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-20 pt-32">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="hero-badge-anim flex items-center gap-3 mb-10">
              <div className="w-px h-8 bg-accent-green/50" />
              <div className="flex items-center gap-2">
                <Leaf className="w-3.5 h-3.5 text-emerald-400/90" />
                <span className="text-emerald-200 text-sm font-semibold tracking-[0.2em] uppercase">
                  ISO & GMP Certified · Est. 2018
                </span>
              </div>
            </div>

            <div className="hero-title-anim mb-4">
              <h1
                data-testid="hero-title"
                className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] text-emerald-50 leading-[1.02] tracking-tighter"
              >
                {lang === "en" ? "Science-Backed" : "科学营养"}
                <br />
                <span className="text-emerald-300">
                  {lang === "en" ? "Nutrition" : "守护"}
                </span>
              </h1>
            </div>

            <div className="hero-sub-anim mb-8 h-12">
              <span className="font-heading font-medium text-xl sm:text-2xl lg:text-3xl text-emerald-50/90">
                {lang === "en" ? "for " : "为了 "}
                <span className="text-emerald-300">
                  <TypeWriter texts={rotatingWords} />
                </span>
              </span>
            </div>

            <p
              data-testid="hero-subtitle"
              className="hero-sub-anim text-emerald-50/80 text-base sm:text-lg leading-relaxed max-w-lg mb-10"
            >
              {t.hero.subtitle}
            </p>

            <div className="hero-cta-anim flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="hero-cta-products"
                onClick={() => scrollTo("products")}
                className="bg-accent-green hover:bg-accent-green/90 text-white font-semibold px-7 py-6 rounded-xl text-base transition-all duration-300 hover:shadow-[0_0_32px_rgba(46,204,113,0.35)] hover:scale-[1.02] group"
              >
                {t.hero.cta}
                <ChevronRight className="w-4 h-4 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              <Button
                data-testid="hero-cta-contact"
                variant="outline"
                onClick={() => scrollTo("contact")}
                className="border-emerald-50/30 text-emerald-50 hover:bg-emerald-50/10 hover:border-emerald-50/40 px-7 py-6 rounded-xl text-base bg-transparent transition-all duration-300"
              >
                {t.hero.ctaSecondary}
              </Button>
            </div>
          </div>

          <div className="hidden lg:flex lg:col-span-4 flex-col items-end gap-0">
            {[
              { val: "50+", label: lang === "en" ? "Countries" : "服务国家" },
              { val: "30+", label: lang === "en" ? "Products" : "产品系列" },
              { val: "100%", label: lang === "en" ? "Traceable" : "可追溯" }
            ].map((stat, i) => (
              <div
                key={i}
                className="hero-image-anim w-full border-l-2 border-accent-green/30 pl-5 py-5 hover:border-accent-green/50 transition-colors duration-500 group"
                style={{ animationDelay: `${0.6 + i * 0.15}s` }}
              >
                <div className="text-emerald-300 font-heading font-bold text-3xl lg:text-4xl group-hover:text-emerald-200 transition-colors">{stat.val}</div>
                <div className="text-emerald-50/70 text-sm mt-1 font-medium tracking-wider uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        data-testid="scroll-indicator"
        onClick={() => scrollTo("about")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-emerald-50/30 hover:text-emerald-50/50 transition-colors"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </button>
    </section>
  );
}
